import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Buildings, Printer, LockKey, Receipt } from "@phosphor-icons/react";
import { api, formatApiError } from "../lib/api";
import { formatINR, formatDate, formatDateTime } from "../lib/format";
import PageHeader from "../components/layout/PageHeader";
import { Button } from "../components/ui/button";
import StatusBadge from "../components/StatusBadge";
import { toast } from "sonner";

export default function CreditNoteDetail() {
    const { id } = useParams();
    const [cn, setCn] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get(`/billing/credit-note/${id}`);
                setCn(data);
            } catch (err) {
                toast.error(formatApiError(err, "Failed to load credit note"));
            } finally {
                setLoading(false);
            }
        })();
    }, [id]);

    if (loading) return <div data-testid="cn-detail-page"><PageHeader eyebrow="Credit note" title="Loading…" /></div>;
    if (!cn) return <div className="p-8 text-sm text-slate-500">Credit note not found.</div>;

    const isIntra = cn.gst_breakup.cgst > 0 || cn.gst_breakup.sgst > 0;

    return (
        <div data-testid="cn-detail-page">
            <PageHeader
                eyebrow={
                    <Link to="/credit-notes" className="inline-flex items-center gap-1 hover:text-slate-900">
                        <ArrowLeft size={12} /> Credit notes
                    </Link>
                }
                title={
                    <span className="flex items-center gap-3 flex-wrap">
                        <span className="font-mono text-2xl sm:text-3xl">{cn.cn_number}</span>
                        <StatusBadge status="Locked" icon={<LockKey size={10} weight="fill" />}>IMMUTABLE</StatusBadge>
                    </span>
                }
                description={
                    <span className="inline-flex items-center gap-2 flex-wrap">
                        <Buildings size={14} /> {cn.client?.name}
                        {cn.invoice && (
                            <>
                                <span className="text-slate-300">·</span>
                                <Link to={`/invoices/${cn.invoice.id}`} className="inline-flex items-center gap-1 hover:text-slate-900 underline">
                                    <Receipt size={12} /> Against {cn.invoice.invoice_number}
                                </Link>
                            </>
                        )}
                    </span>
                }
                actions={
                    <Link to={`/credit-notes/${cn.id}/print`}>
                        <Button variant="outline" className="gap-1.5" data-testid="cn-print-btn">
                            <Printer size={14} weight="fill" /> Print / PDF
                        </Button>
                    </Link>
                }
            />

            <div className="px-6 sm:px-8 py-8 grid grid-cols-1 lg:grid-cols-[1fr,320px] gap-6">
                <div className="space-y-6">
                    {/* Meta */}
                    <div className="reedos-card">
                        <div className="grid grid-cols-2 sm:grid-cols-4 divide-x divide-slate-200">
                            {[
                                { label: "CN date", value: formatDate(cn.cn_date) },
                                { label: "Invoice", value: cn.invoice ? <Link to={`/invoices/${cn.invoice.id}`} className="font-mono text-slate-900 hover:underline">{cn.invoice.invoice_number}</Link> : "—" },
                                { label: "Reason", value: cn.reason_label },
                                { label: "Amount (in words)", value: <span className="text-xs">{cn.amount_in_words}</span> },
                            ].map((c) => (
                                <div key={c.label} className="px-4 py-3">
                                    <div className="reedos-eyebrow">{c.label}</div>
                                    <div className="text-sm text-slate-900 mt-1">{c.value}</div>
                                </div>
                            ))}
                        </div>
                        {/* Iter 50: REEDOS Proposal No — originating reference, inherited from source invoice */}
                        {cn.proposal_number && (
                            <div
                                className="px-4 py-2.5 border-t border-slate-200 bg-slate-50/60 flex items-center gap-2 text-[11px]"
                                data-testid="cn-reedos-proposal-no"
                                title="REEDOS Proposal No — originating proposal reference, snapshot from the source invoice."
                            >
                                <span className="reedos-eyebrow text-slate-500">REEDOS Proposal No</span>
                                {cn.proposal_id ? (
                                    <Link to={`/proposals/${cn.proposal_id}`} className="font-mono text-slate-700 hover:text-slate-900 hover:underline">
                                        {cn.proposal_number}
                                    </Link>
                                ) : (
                                    <span className="font-mono text-slate-700">{cn.proposal_number}</span>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Reason notes */}
                    <div className="reedos-card p-5">
                        <div className="reedos-eyebrow">Reason notes</div>
                        <p className="text-sm text-slate-800 mt-2 whitespace-pre-wrap" data-testid="cn-reason-notes">{cn.reason_notes}</p>
                    </div>

                    {/* Line items */}
                    <div className="reedos-card">
                        <div className="px-5 py-4 border-b border-slate-200">
                            <div className="reedos-eyebrow">Credit lines</div>
                            <h3 className="font-display text-base font-semibold text-slate-900 mt-0.5">{cn.line_items.length} item{cn.line_items.length !== 1 ? "s" : ""}</h3>
                        </div>
                        <table className="w-full text-sm" data-testid="cn-lines-table">
                            <thead className="bg-slate-50 border-b border-slate-200">
                                <tr className="text-left">
                                    <th className="px-5 py-3 reedos-eyebrow">Description</th>
                                    <th className="px-5 py-3 reedos-eyebrow">HSN/SAC</th>
                                    <th className="px-5 py-3 reedos-eyebrow text-right">Qty</th>
                                    <th className="px-5 py-3 reedos-eyebrow text-right">Amount</th>
                                    <th className="px-5 py-3 reedos-eyebrow text-right">GST</th>
                                    <th className="px-5 py-3 reedos-eyebrow text-right">Tax</th>
                                    <th className="px-5 py-3 reedos-eyebrow text-right">Total</th>
                                </tr>
                            </thead>
                            <tbody>
                                {cn.line_items.map((li) => (
                                    <tr key={li.id} className="border-t border-slate-100">
                                        <td className="px-5 py-3 text-slate-900">{li.description}</td>
                                        <td className="px-5 py-3 text-xs font-mono text-slate-600">{li.hsn_sac_code || "—"}</td>
                                        <td className="px-5 py-3 text-right reedos-data">{li.quantity}</td>
                                        <td className="px-5 py-3 text-right reedos-data">{formatINR(li.amount, cn?.currency)}</td>
                                        <td className="px-5 py-3 text-right text-xs text-slate-600">{li.gst_percent}%</td>
                                        <td className="px-5 py-3 text-right reedos-data text-slate-700">{formatINR(li.gst_amount, cn?.currency)}</td>
                                        <td className="px-5 py-3 text-right reedos-data font-semibold">{formatINR(li.total_amount, cn?.currency)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        <div className="border-t border-slate-200 bg-slate-50 px-5 py-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div />
                            <div className="space-y-1.5">
                                <div className="flex justify-between text-sm"><span className="text-slate-500">Subtotal</span><span className="reedos-data">{formatINR(cn.subtotal, cn?.currency)}</span></div>
                                {isIntra ? (
                                    <>
                                        <div className="flex justify-between text-xs"><span className="text-slate-500">CGST</span><span className="reedos-data">{formatINR(cn.gst_breakup.cgst, cn?.currency)}</span></div>
                                        <div className="flex justify-between text-xs"><span className="text-slate-500">SGST</span><span className="reedos-data">{formatINR(cn.gst_breakup.sgst, cn?.currency)}</span></div>
                                    </>
                                ) : (
                                    <div className="flex justify-between text-xs"><span className="text-slate-500">IGST</span><span className="reedos-data">{formatINR(cn.gst_breakup.igst, cn?.currency)}</span></div>
                                )}
                                <div className="flex justify-between text-sm font-display font-bold pt-2 border-t border-slate-200">
                                    <span>Total credited</span>
                                    <span className="reedos-data text-base text-rose-700" data-testid="cn-total">{formatINR(cn.total_amount, cn?.currency)}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Right column */}
                <div className="space-y-6">
                    <div className="reedos-card">
                        <div className="px-5 py-4 border-b border-slate-200">
                            <div className="reedos-eyebrow">Credit to</div>
                            <h3 className="font-display text-base font-semibold text-slate-900 mt-0.5">{cn.client?.name}</h3>
                        </div>
                        <div className="p-5 space-y-2 text-sm">
                            {cn.client?.gstin && <div className="font-mono text-xs">{cn.client.gstin}</div>}
                            {cn.client?.address && <div className="text-slate-600 text-xs">{cn.client.address}</div>}
                        </div>
                    </div>
                    <div className="reedos-card p-5 text-xs text-slate-500 space-y-1">
                        <div><span className="reedos-eyebrow">Issued at</span> <span className="ml-2">{formatDateTime(cn.created_at)}</span></div>
                        <div><span className="reedos-eyebrow">Issued by</span> <span className="ml-2 font-mono">{cn.created_by}</span></div>
                    </div>
                </div>
            </div>
        </div>
    );
}
